import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
  Grid,
  Alert,
  Snackbar,
  Tooltip
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  ContentCopy,
  Visibility,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL } from '../config/api';

interface TestConfig {
  id: string;
  name: string;
  description?: string;
  type: 'smoketest' | 'regression' | 'fulltest';
  devices?: string[];
  maxDevices?: number;
  integrations?: ('square' | 'toast' | 'lightspeed' | 'clover' | 'non-integration')[];
  environments?: ('production' | 'beta' | 'production-new' | 'staging')[];
  integrationConcurrency?: number;
  continueOnFail?: boolean;
  retries?: number;
  runSQLValidation?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  isDefault?: boolean;
}

export default function TestConfigManagement() {
  const { t } = useTranslation();
  const [configs, setConfigs] = useState<TestConfig[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConfig, setEditingConfig] = useState<TestConfig | null>(null);
  const [selectedType, setSelectedType] = useState<'smoketest' | 'regression' | 'fulltest' | 'all'>('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });

  // 表单状态
  const [formData, setFormData] = useState<Partial<TestConfig>>({
    name: '',
    description: '',
    type: 'smoketest',
    maxDevices: 1,
    integrationConcurrency: 1,
    continueOnFail: false,
    retries: 1,
    runSQLValidation: true
  });

  const availableIntegrations = ['square', 'toast', 'lightspeed', 'clover', 'non-integration'];
  const availableEnvironments = ['production', 'beta', 'production-new', 'staging'];

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const url = selectedType === 'all' 
        ? `${API_BASE_URL}/api/test-configs`
        : `${API_BASE_URL}/api/test-configs?type=${selectedType}`;
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setConfigs(data.data || []);
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: `${t('testConfigManagement.messages.loadFailed')}: ${error.message}`, severity: 'error' });
    }
  };

  useEffect(() => {
    fetchConfigs();
  }, [selectedType]);

  const handleOpenDialog = (config?: TestConfig) => {
    if (config) {
      // 🎯 修复：所有配置（包括默认配置）都设置editingConfig，以便更新而不是创建
      setEditingConfig(config);
      // 🎯 关键修复：确保所有字段都正确设置，包括integrations和environments数组
      // 使用原始存储的值，确保编辑对话框正确显示当前配置
      setFormData({
        name: config.name,
        description: config.description || '',
        type: config.type,
        devices: Array.isArray(config.devices) ? [...config.devices] : [],
        maxDevices: config.maxDevices || 1,
        integrations: Array.isArray(config.integrations) ? [...config.integrations] : [],
        environments: Array.isArray(config.environments) ? [...config.environments] : [],
        integrationConcurrency: config.integrationConcurrency || 1,
        continueOnFail: config.continueOnFail || false,
        retries: config.retries || 1,
        runSQLValidation: config.runSQLValidation !== false
      });
    } else {
      setEditingConfig(null);
      setFormData({
        name: '',
        description: '',
        type: selectedType !== 'all' ? selectedType : 'smoketest',
        maxDevices: 1,
        integrationConcurrency: 1,
        continueOnFail: false,
        retries: 1,
        runSQLValidation: true,
        integrations: [],
        environments: []
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      type: 'smoketest',
      maxDevices: 1,
      integrationConcurrency: 1,
      continueOnFail: false,
      retries: 1,
      runSQLValidation: true
    });
  };

  const handleSave = async () => {
    try {
      // 🎯 关键修复：确保所有字段都正确传递，特别是 integrations 和 environments
      // 如果 integrations 或 environments 为空数组，应该传递空数组而不是 undefined
      const payload = {
        ...formData,
        integrations: Array.isArray(formData.integrations) ? formData.integrations : [],
        environments: Array.isArray(formData.environments) ? formData.environments : [],
        devices: Array.isArray(formData.devices) ? formData.devices : []
      };

      if (editingConfig) {
        // 更新配置
        console.log('🔄 [TestConfig] 更新配置:', editingConfig.id, payload);
        const response = await fetch(`${API_BASE_URL}/api/test-configs/${editingConfig.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (data.success) {
          setSnackbar({ open: true, message: t('testConfigManagement.messages.configUpdated'), severity: 'success' });
          fetchConfigs();
          handleCloseDialog();
        } else {
          setSnackbar({ open: true, message: data.error || t('testConfigManagement.messages.updateFailed'), severity: 'error' });
          console.error('❌ [TestConfig] 更新失败:', data);
        }
      } else {
        // 创建配置
        console.log('🆕 [TestConfig] 创建配置:', payload);
        const response = await fetch(`${API_BASE_URL}/api/test-configs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json();
        if (data.success) {
          setSnackbar({ open: true, message: t('testConfigManagement.messages.configCreated'), severity: 'success' });
          fetchConfigs();
          handleCloseDialog();
        } else {
          setSnackbar({ open: true, message: data.error || t('testConfigManagement.messages.createFailed'), severity: 'error' });
          console.error('❌ [TestConfig] 创建失败:', data);
        }
      }
    } catch (error: any) {
      console.error('❌ [TestConfig] 保存失败:', error);
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('testConfigManagement.messages.confirmDelete'))) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/test-configs/${id}`, {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: t('testConfigManagement.messages.configDeleted'), severity: 'success' });
        fetchConfigs();
      } else {
        setSnackbar({ open: true, message: data.error || t('testConfigManagement.messages.deleteFailed'), severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const handleDuplicate = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/test-configs/${id}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ newName: undefined })
      });
      const data = await response.json();
      if (data.success) {
        setSnackbar({ open: true, message: t('testConfigManagement.messages.configDuplicated'), severity: 'success' });
        fetchConfigs();
      } else {
        setSnackbar({ open: true, message: data.error || t('testConfigManagement.messages.duplicateFailed'), severity: 'error' });
      }
    } catch (error: any) {
      setSnackbar({ open: true, message: error.message, severity: 'error' });
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      smoketest: t('testConfigManagement.smoketest'),
      regression: t('testConfigManagement.regression'),
      fulltest: t('testConfigManagement.fulltest')
    };
    return labels[type] || type;
  };

  const getConfigName = (config: TestConfig) => {
    // 如果是默认配置，使用翻译
    if (config.isDefault && config.id) {
      const translationKey = `testConfigManagement.defaultConfigs.${config.id}.name`;
      const translated = t(translationKey);
      // 如果翻译存在且不是翻译键本身，使用翻译
      if (translated !== translationKey) {
        return translated;
      }
    }
    return config.name;
  };

  const getConfigDescription = (config: TestConfig) => {
    // 如果是默认配置，使用翻译
    if (config.isDefault && config.id) {
      const translationKey = `testConfigManagement.defaultConfigs.${config.id}.description`;
      const translated = t(translationKey);
      // 如果翻译存在且不是翻译键本身，使用翻译
      if (translated !== translationKey) {
        return translated;
      }
    }
    return config.description || '';
  };

  const filteredConfigs = configs.filter(c => selectedType === 'all' || c.type === selectedType);

  return (
      <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('testConfigManagement.title')}</Typography>
        <Box>
          <FormControl sx={{ minWidth: 150, mr: 2 }}>
            <InputLabel>{t('testConfigManagement.testType')}</InputLabel>
            <Select
              value={selectedType}
              label={t('testConfigManagement.testType')}
              onChange={(e) => setSelectedType(e.target.value as any)}
            >
              <MenuItem value="all">{t('testConfigManagement.all')}</MenuItem>
              <MenuItem value="smoketest">{t('testConfigManagement.smoketest')}</MenuItem>
              <MenuItem value="regression">{t('testConfigManagement.regression')}</MenuItem>
              <MenuItem value="fulltest">{t('testConfigManagement.fulltest')}</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            {t('testConfigManagement.newConfig')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('testConfigManagement.columns.name')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.type')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.maxDevices')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.integrations')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.environments')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.concurrency')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.retries')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.sqlValidation')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.default')}</TableCell>
              <TableCell>{t('testConfigManagement.columns.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredConfigs.map((config) => (
              <TableRow key={config.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    {getConfigName(config)}
                  </Typography>
                  {getConfigDescription(config) && (
                    <Typography variant="caption" color="text.secondary">
                      {getConfigDescription(config)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{getTypeLabel(config.type)}</TableCell>
                <TableCell>{config.maxDevices || '-'}</TableCell>
                <TableCell>
                  {config.integrations && config.integrations.length > 0 ? (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {config.integrations.slice(0, 3).map((int) => (
                        <Chip key={int} label={int} size="small" />
                      ))}
                      {config.integrations.length > 3 && (
                        <Chip label={`+${config.integrations.length - 3}`} size="small" />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">{t('testConfigManagement.labels.allIntegrations')}</Typography>
                  )}
                </TableCell>
                <TableCell>
                  {config.environments && config.environments.length > 0 ? (
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {config.environments.slice(0, 3).map((env) => (
                        <Chip key={env} label={env} size="small" color="primary" variant="outlined" />
                      ))}
                      {config.environments.length > 3 && (
                        <Chip label={`+${config.environments.length - 3}`} size="small" />
                      )}
                    </Box>
                  ) : (
                    <Typography variant="caption" color="text.secondary">{t('testConfigManagement.labels.allEnvironments')}</Typography>
                  )}
                </TableCell>
                <TableCell>{config.integrationConcurrency || '-'}</TableCell>
                <TableCell>{config.retries || '-'}</TableCell>
                <TableCell>
                  {config.runSQLValidation ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <ErrorIcon color="disabled" fontSize="small" />
                  )}
                </TableCell>
                <TableCell>
                  {config.isDefault ? (
                    <Chip label={t('testConfigManagement.labels.default')} size="small" color="primary" />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5}>
                    {/* 🎯 修复：所有配置都显示编辑按钮，默认配置编辑时会自动创建副本 */}
                    <Tooltip title={config.isDefault ? t('testConfigManagement.messages.editDefaultHint', '编辑默认配置将创建副本') : t('testConfigManagement.labels.edit')}>
                      <IconButton size="small" onClick={() => handleOpenDialog(config)}>
                        <Edit />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('testConfigManagement.labels.copy')}>
                      <IconButton size="small" onClick={() => handleDuplicate(config.id)}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                    {/* 🎯 默认配置不显示删除按钮 */}
                    {!config.isDefault && (
                      <Tooltip title={t('testConfigManagement.labels.delete')}>
                        <IconButton size="small" color="error" onClick={() => handleDelete(config.id)}>
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* 编辑/创建对话框 */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingConfig ? t('testConfigManagement.editConfig') : t('testConfigManagement.newConfig')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('testConfigManagement.form.configName')}
                value={formData.name || ''}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('testConfigManagement.form.description')}
                value={formData.description || ''}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={2}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>{t('testConfigManagement.form.testType')}</InputLabel>
                <Select
                  value={formData.type || 'smoketest'}
                  label={t('testConfigManagement.form.testType')}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                  disabled={!!editingConfig}
                >
                  <MenuItem value="smoketest">{t('testConfigManagement.smoketest')}</MenuItem>
                  <MenuItem value="regression">{t('testConfigManagement.regression')}</MenuItem>
                  <MenuItem value="fulltest">{t('testConfigManagement.fulltest')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                type="number"
                label={t('testConfigManagement.form.maxDevices')}
                value={formData.maxDevices || 1}
                onChange={(e) => setFormData({ ...formData, maxDevices: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('testConfigManagement.form.integrationsHint')}</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {availableIntegrations.map((int) => (
                  <FormControlLabel
                    key={int}
                    control={
                      <Checkbox
                        checked={formData.integrations?.includes(int as any) || false}
                        onChange={(e) => {
                          const current = formData.integrations || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, integrations: [...current, int as any] });
                          } else {
                            setFormData({ ...formData, integrations: current.filter(i => i !== int) });
                          }
                        }}
                      />
                    }
                    label={int}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>{t('testConfigManagement.form.environmentsHint')}</Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {availableEnvironments.map((env) => (
                  <FormControlLabel
                    key={env}
                    control={
                      <Checkbox
                        checked={formData.environments?.includes(env as any) || false}
                        onChange={(e) => {
                          const current = formData.environments || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, environments: [...current, env as any] });
                          } else {
                            setFormData({ ...formData, environments: current.filter(e => e !== env) });
                          }
                        }}
                      />
                    }
                    label={env}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label={t('testConfigManagement.form.integrationConcurrency')}
                value={formData.integrationConcurrency || 1}
                onChange={(e) => setFormData({ ...formData, integrationConcurrency: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 1 }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                type="number"
                label={t('testConfigManagement.form.retries')}
                value={formData.retries || 1}
                onChange={(e) => setFormData({ ...formData, retries: parseInt(e.target.value) || 1 })}
                inputProps={{ min: 0 }}
              />
            </Grid>
            <Grid item xs={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.continueOnFail || false}
                    onChange={(e) => setFormData({ ...formData, continueOnFail: e.target.checked })}
                  />
                }
                label={t('testConfigManagement.form.continueOnFail')}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.runSQLValidation !== false}
                    onChange={(e) => setFormData({ ...formData, runSQLValidation: e.target.checked })}
                  />
                }
                label={t('testConfigManagement.form.runSQLValidation')}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('testConfigManagement.labels.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name}>
            {editingConfig ? t('testConfigManagement.labels.update') : t('testConfigManagement.labels.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

